import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import "./App.css";
import Navbar from "./components/Navbar.tsx";
// import RightSidebar from "./components/RightSidebar.tsx";
import { supabase } from "./SupabaseClient.tsx";
import { getCheckoutUrl, Lapse } from "./utils/checkout.ts";

import ReceiptLibrary from "./pages/ReceiptLibrary.tsx";
import LandingPage from "./pages/LandingPage.tsx";
import Settings from "./pages/Settings.tsx";
import ReceiptDetail from "./pages/ReceiptDetail.tsx";
import ReceiptUpload from "./pages/ReceiptUpload.tsx";
import Loading from "./pages/Loading.tsx";
import Reimburse from "./pages/Reimburse.tsx";
import TaxCenter from "./pages/TaxCenter.tsx";
import UpdatePassword from "./pages/UpdatePassword.tsx";
import Dashboard from "./pages/Dashboard.tsx";
import AccessEnded from "./pages/AccessEnded.tsx";
import Unsubscribe from "./pages/Unsubscribe.tsx";
import ProjectSavings from "./pages/ProjectSavings.tsx";
import OnboardingModal from "./components/OnboardingModal.tsx";
import { recordNewSignup } from "./utils/firstTouch.ts";

function MainApp() {
  const [, setIsNavbarCollapsed] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  const [trialEndsAt, setTrialEndsAt] = useState<number | null>(null);
  const [userEmail, setUserEmail] = useState<string | undefined>();
  // Set when a trial or subscription has ended, which makes the account read-only
  const [lapse, setLapse] = useState<Lapse | null>(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(false);

  useEffect(() => {
    const checkOnboardingStatus = async (userId: string) => {
      try {
        const { data, error } = await supabase
          .from("onboarding")
          .select("webapp_referral_source")
          .eq("user_id", userId)
          .single();

        if (error) {
          console.error("Error checking onboarding status:", error);
          return;
        }

        // Show onboarding if webapp_referral_source is null
        if (data && data.webapp_referral_source === null) {
          setShowOnboarding(true);
        }
      } catch (error) {
        console.error("Error checking onboarding status:", error);
      }
    };

    const checkSubscription = (session) => {
      if (!session) {
        supabase.auth.signOut();
        return Promise.resolve();
      }

      setLoading(true);
      return supabase.functions
        .invoke("check-subscription", {
          method: "GET",
        })
        .then((response) => {
          if (response) {
            const { hasActiveSubscription, trialEndsAt, lapseReason } =
              response.data;
            if (hasActiveSubscription) {
              setHasActiveSubscription(true);
              setLapse(null);
              setTrialEndsAt(trialEndsAt ?? null);
              setUserEmail(session.user.email);
              setIsAuthenticated(true);
              // Check onboarding status after successful authentication
              checkOnboardingStatus(session.user.id);
              recordNewSignup(session.user);
            } else if (lapseReason) {
              // They stay signed in to view and download what they've saved
              setLapse({ reason: lapseReason, email: session.user.email });
              setIsAuthenticated(true);
            } else {
              supabase.auth.signOut().then(() => {
                window.location.href = getCheckoutUrl(session.user.email);
              });
            }
          } else {
            console.error("Error checking subscription:", response);
          }
        })
        .catch((error) => {
          console.error("Error invoking function:", error);
        })
        .finally(() => {
          setLoading(false);
        });
    };

    const { data: authListener } = supabase.auth.onAuthStateChange(
      (event, session) => {
        // Handle password recovery flow - bypass subscription check
        if (event === "PASSWORD_RECOVERY") {
          setIsPasswordRecovery(true);
          setIsAuthenticated(true);
          setLoading(false);
          return;
        }

        if (session) {
          // Skip subscription check if in password recovery mode
          if (isPasswordRecovery) {
            setLoading(false);
            return;
          }

          if (event === "INITIAL_SESSION") {
            checkSubscription(session);
          } else if (event === "SIGNED_IN") {
            checkSubscription(session);
          } else if (event === "TOKEN_REFRESHED") {
            setIsAuthenticated(true);
            setLoading(false);
          }
        } else {
          // User is signed out
          setIsAuthenticated(false);
          setHasActiveSubscription(false);
          setLapse(null);
          setIsPasswordRecovery(false);
          setLoading(false);
        }
      }
    );

    // Cleanup subscription on unmount
    return () => {
      authListener?.subscription?.unsubscribe();
    };
  }, [isPasswordRecovery]);

  useEffect(() => {
    document.body.classList.add("right-sidebar-collapsed");
  }, []);

  // useEffect(() => {
  //   if (isNavbarCollapsed) {
  //     document.body.classList.add("navbar-collapsed");
  //   } else {
  //     document.body.classList.remove("navbar-collapsed");
  //   }
  // }, [isNavbarCollapsed]);

  const handleOnboardingComplete = async (source: string) => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) {
        console.error("No user found");
        return;
      }

      const { error } = await supabase
        .from("onboarding")
        .update({ webapp_referral_source: source })
        .eq("user_id", user.id);

      if (error) {
        console.error("Error updating onboarding:", error);
      } else {
        console.log("Onboarding completed successfully");
      }
    } catch (error) {
      console.error("Error completing onboarding:", error);
    } finally {
      setShowOnboarding(false);
    }
  };

  if (loading) {
    return <Loading />;
  }

  // Show UpdatePassword page for password recovery flow (bypass subscription)
  if (isPasswordRecovery) {
    return (
      <Router>
        <div className="App">
          <Routes>
            <Route path="/update-password" element={<UpdatePassword />} />
            <Route path="*" element={<Navigate to="/update-password" />} />
          </Routes>
        </div>
      </Router>
    );
  }

  return (
    <Router>
      <div className="App">
        {isAuthenticated && lapse ? (
          <Routes>
            <Route path="/update-password" element={<UpdatePassword />} />
            <Route
              path="*"
              element={
                <>
                  <Navbar onCollapse={setIsNavbarCollapsed} readOnly={true} />
                  <Routes>
                    <Route path="/" element={<AccessEnded lapse={lapse} />} />
                    <Route
                      path="/receipt-library"
                      element={<ReceiptLibrary lapse={lapse} />}
                    />
                    <Route
                      path="/receipt-library/:id"
                      element={<ReceiptDetail lapse={lapse} />}
                    />
                    <Route
                      path="/tax-center"
                      element={<TaxCenter lapse={lapse} />}
                    />
                    <Route path="/settings" element={<Settings />} />
                    <Route path="*" element={<Navigate to="/" />} />
                  </Routes>
                </>
              }
            />
          </Routes>
        ) : isAuthenticated && hasActiveSubscription ? (
          <>
            <OnboardingModal
              isOpen={showOnboarding}
              onComplete={handleOnboardingComplete}
              // Asked again next sign-in, so someone can get to their first receipt first
              onSkip={() => setShowOnboarding(false)}
            />
            <Routes>
              {/* Special route without Navbar */}
              <Route path="/update-password" element={<UpdatePassword />} />

              {/* Routes with Navbar */}
              <Route
                path="*"
                element={
                  <>
                    <Navbar onCollapse={setIsNavbarCollapsed} />
                    <Routes>
                      <Route
                        path="/"
                        element={
                          <Dashboard
                            trialEndsAt={trialEndsAt}
                            userEmail={userEmail}
                          />
                        }
                      />
                      <Route
                        path="/receipt-upload"
                        element={<ReceiptUpload />}
                      />
                      <Route
                        path="/receipt-library"
                        element={<ReceiptLibrary />}
                      />
                      <Route
                        path="/receipt-library/:id"
                        element={<ReceiptDetail />}
                      />
                      <Route path="/reimburse" element={<Reimburse />} />
                      <Route path="/tax-center" element={<TaxCenter />} />
                      <Route
                        path="/project-savings"
                        element={<ProjectSavings />}
                      />
                      <Route path="/settings" element={<Settings />} />
                      <Route path="*" element={<Navigate to="/" />} />
                    </Routes>
                    {/* <RightSidebar isNavbarCollapsed={isNavbarCollapsed} /> */}
                  </>
                }
              />
            </Routes>
          </>
        ) : (
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/signup" element={<LandingPage startInSignUp />} />
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        )}
      </div>
    </Router>
  );
}

// Kept outside the main app, because checking a signed-in visitor's subscription can start a trial
function App() {
  return window.location.pathname === "/unsubscribe" ? <Unsubscribe /> : <MainApp />;
}

export default App;
